import React, { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, BarChart3, TrendingUp, Clock, AlertCircle, CheckCircle2, Sparkles } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { fmtDate } from "@/utils/date";
import SubmissionsArchive from "@/components/SubmissionsArchive";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";
import { STATUS_META } from "@/constants/status";
import { ClientTag } from "@/components/ClientTag";

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayISO() { return iso(new Date()); }
function daysAgoISO(n) { return iso(new Date(Date.now() - n * 86400000)); }
function startOfMonthISO() { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); }
function endOfMonthISO() { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)); }
function startOfYearISO() { const d = new Date(); return iso(new Date(d.getFullYear(), 0, 1)); }
function endOfYearISO() { const d = new Date(); return iso(new Date(d.getFullYear(), 11, 31)); }
function startOfWeekISO() {
  const d = new Date(); const day = d.getDay(); const diff = (day + 6) % 7; // Monday start
  return iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff));
}

const PRESETS = [
  { key: "week",  label: "This Week",   from: startOfWeekISO,   to: todayISO },
  { key: "month", label: "This Month",  from: startOfMonthISO,  to: endOfMonthISO },
  { key: "year",  label: "This Year",   from: startOfYearISO,   to: endOfYearISO },
  { key: "30d",   label: "Last 30 days",from: () => daysAgoISO(29), to: todayISO },
];

const STATUS_COLORS = Object.fromEntries(Object.entries(STATUS_META).map(([k, v]) => [k, v.hex]));

export default function Reports() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || !!user?.is_super_admin;
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [preset, setPreset] = useState("month");
  const [dateFrom, setDateFrom] = useState(startOfMonthISO());
  const [dateTo, setDateTo] = useState(endOfMonthISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [concern, setConcern] = useState("");
  const [sendingConcern, setSendingConcern] = useState(false);

  useEffect(() => {
    // Editors can only see themselves — skip the /users call and lock userId to self.
    if (!isManager) {
      setUsers([{ id: user.id, name: user.name, title: user.title || "", role: user.role, team: user.team }]);
      setUserId(user.id);
      return;
    }
    api.get("/users").then(({ data }) => {
      setUsers(data);
      const me = data.find((u) => u.id === user?.id);
      setUserId(me?.id || data[0]?.id || "");
    }).catch(() => {});
  }, [user, isManager]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get("/reports/kra", { params: { user_id: userId, date_from: dateFrom, date_to: dateTo } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, dateFrom, dateTo]);

  const setRange = (p) => {
    setPreset(p.key);
    setDateFrom(p.from());
    setDateTo(p.to());
  };

  const selectedUser = users.find((u) => u.id === userId);

  const statusData = useMemo(() => data ? [
    { name: "Complete",      value: data.complete,             color: STATUS_COLORS.complete },
    { name: "In Progress",   value: data.in_progress,          color: STATUS_COLORS.in_progress },
    { name: "Carry Forward", value: data.carry_forward || 0,   color: STATUS_COLORS.carry_forward },
    { name: "On Hold",       value: data.on_hold || 0,         color: STATUS_COLORS.on_hold },
    { name: "Not Started",   value: data.not_started,          color: STATUS_COLORS.not_started },
    { name: "Incomplete",    value: data.incomplete,           color: STATUS_COLORS.incomplete },
  ] : [], [data]);

  const stats = data ? [
    { label: "Total Tasks",     value: data.total_tasks, icon: BarChart3, color: "text-white" },
    { label: "Complete",        value: data.complete,    icon: CheckCircle2, color: "text-emerald-400" },
    { label: "Incomplete",      value: data.incomplete,  icon: AlertCircle,  color: "text-amber-400" },
    { label: "In Progress",     value: data.in_progress, icon: TrendingUp,   color: "text-blue-400" },
    { label: "Other Work Done", value: data.extra_tasks_total, icon: Sparkles, color: "text-[#E11D2E]" },
    { label: "OT Half-Days",    value: data.ot_half_days,icon: Clock, color: "text-white/70" },
    { label: "OT Full-Days",    value: data.ot_full_days,icon: Clock, color: "text-white/70" },
    { label: "Leaves",          value: data.leaves,      icon: Clock, color: "text-white/70" },
    { label: "Weekly Off",      value: data.weekly_offs || 0, icon: Clock, color: "text-white/70" },
    { label: "Holidays",        value: data.holidays || 0,    icon: Clock, color: "text-white/70" },
    { label: "OT on Off-Days",  value: data.ot_on_off_days || 0, icon: Clock, color: "text-amber-300" },
    { label: "Days Tracked",    value: (data.total_tracked_days || 0) === 0 ? "—" : (data.total_tracked_days === 0.5 ? "½ day" : data.total_tracked_days === 1 ? "1 day" : `${data.total_tracked_days} days`), icon: Clock, color: "text-sky-300" },
    { label: "Hours Tracked",   value: (data.total_tracked_hours || 0) ? `${data.total_tracked_hours}h` : "—", icon: Clock, color: "text-blue-300" },
    { label: "Logs Filed",      value: data.logs_filed,  icon: BarChart3, color: "text-white/70" },
  ] : [];

  const downloadPdf = async (params, filename) => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/monthly-pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to generate PDF"); }
    finally { setDownloading(false); }
  };

  // Backend PDF uses YYYY-MM (month) — pick the month of the selected range's start.
  const pdfMonth = dateFrom.slice(0, 7);
  const uName = (selectedUser?.name || "user").replace(/\W+/g, "-");

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap gap-4 justify-between items-end mb-6">
        <div>
          <div className="overline flex items-center gap-2"><BarChart3 size={12} /> Reports & KRA</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">Performance ledger</h1>
          <p className="text-sm text-white/50 mt-1">Week, month, year, or a custom range — with graphs and downloadable PDFs.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isManager && (
            <Button
              data-testid="report-pdf-combined"
              onClick={() => downloadPdf({ month: pdfMonth }, `LIM-Monthly-${pdfMonth}.pdf`)}
              disabled={downloading}
              className="bg-red-600 hover:bg-red-500 rounded-full"
            >
              <Download size={14} className="mr-2" />
              {downloading ? "Generating…" : "Combined (Team-wise)"}
            </Button>
          )}
          <Button
            data-testid={TID.reportPdfBtn}
            onClick={() => downloadPdf({ month: pdfMonth, user_id: userId }, `LIM-Monthly-${pdfMonth}-${uName}.pdf`)}
            disabled={downloading || !userId}
            variant={isManager ? "outline" : "default"}
            className={isManager
              ? "rounded-full border-white/20 text-white hover:bg-white/5"
              : "bg-red-600 hover:bg-red-500 rounded-full"}
          >
            <Download size={14} className="mr-2" /> {isManager ? "Individual KRA" : "Download my report"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4 mb-6 flex flex-wrap items-end gap-3">
        {isManager && (
          <div className="w-full sm:w-64">
            <Label className="overline">Member</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid={TID.reportUserSelect} className="bg-black/40 border-white/10 mt-2">
                <SelectValue placeholder="Choose team member" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white max-h-80">
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.title || u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-end">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setRange(p)}
              data-testid={`report-preset-${p.key}`}
              className={`text-xs uppercase tracking-widest px-3 h-10 rounded-full border transition-colors ${
                preset === p.key ? "bg-[#E11D2E]/15 border-[#E11D2E]/40 text-white" : "border-white/10 text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >{p.label}</button>
          ))}
        </div>
        <div className="flex gap-2 ml-auto items-end">
          <div>
            <Label className="overline">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => { setPreset("custom"); setDateFrom(e.target.value); }} data-testid="report-date-from" className="bg-black/40 border-white/10 mt-2 h-10 w-40" />
          </div>
          <div>
            <Label className="overline">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => { setPreset("custom"); setDateTo(e.target.value); }} data-testid="report-date-to" className="bg-black/40 border-white/10 mt-2 h-10 w-40" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {stats.map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="glass rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="overline">{label}</div>
                  <Icon size={12} className={color} />
                </div>
                <div className={`font-hero text-3xl mt-1 ${color}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* Graphs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="glass rounded-xl p-4">
              <div className="overline mb-3">Status Breakdown</div>
              {data.total_tasks === 0 ? (
                <div className="h-56 grid place-items-center text-xs text-white/30 italic">No tasks in this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusData.filter((s) => s.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={{ fill: "currentColor", fontSize: 11 }}>
                      {statusData.filter((s) => s.value > 0).map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--line))", color: "rgb(var(--tw-white))", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "rgb(var(--text-2))" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="glass rounded-xl p-4">
              <div className="overline mb-3">Daily Activity</div>
              {(data.timeline || []).length === 0 ? (
                <div className="h-56 grid place-items-center text-xs text-white/30 italic">No log activity in this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={(data.timeline || []).map((d) => ({ ...d, date: fmtDate(d.date) }))} margin={{ left: -20, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.22)" />
                    <XAxis dataKey="date" tick={{ fill: "rgba(128,128,128,0.9)", fontSize: 10 }} />
                    <YAxis tick={{ fill: "rgba(128,128,128,0.9)", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--line))", color: "rgb(var(--tw-white))", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "rgb(var(--text-2))" }} />
                    <Line type="monotone" dataKey="tasks_touched" name="Tasks touched" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="extras" name="Other work" stroke="#E11D2E" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="glass rounded-xl p-4 mb-6">
            <div className="overline mb-3">Attendance snapshot</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={[
                { name: "Leaves", value: data.leaves, color: "#f59e0b" },
                { name: "1/2 Day OT", value: data.ot_half_days, color: "#0891b2" },
                { name: "Full Day OT", value: data.ot_full_days, color: "#E11D2E" },
                { name: "Weekly Off", value: data.weekly_offs || 0, color: "#64748b" },
                { name: "Holidays", value: data.holidays || 0, color: "#0ea5e9" },
                { name: "OT on Off", value: data.ot_on_off_days || 0, color: "#f97316" },
                { name: "Logs Filed", value: data.logs_filed, color: "#10b981" },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.22)" />
                <XAxis dataKey="name" tick={{ fill: "rgba(128,128,128,0.9)", fontSize: 10 }} />
                <YAxis tick={{ fill: "rgba(128,128,128,0.9)", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--line))", color: "rgb(var(--tw-white))", fontSize: 12 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {["#f59e0b", "#0891b2", "#E11D2E", "#64748b", "#0ea5e9", "#f97316", "#10b981"].map((c) => <Cell key={c} fill={c} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="glass rounded-xl p-4">
            <div className="overline mb-3">Task ledger <span className="text-white/70">{selectedUser?.name || "—"}</span> · {fmtDate(dateFrom)} → {fmtDate(dateTo)}</div>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="text-white/50 text-xs uppercase">
                  <tr className="border-b border-white/5">
                    <th className="text-left py-2 pr-4">Client</th>
                    <th className="text-left py-2 pr-4">Title</th>
                    <th className="text-left py-2 pr-4">Deadline</th>
                    <th className="text-left py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(!data.tasks || data.tasks.length === 0) ? (
                    <tr><td colSpan="4" className="py-6 text-white/40 text-center italic">No tasks in this range.</td></tr>
                  ) : data.tasks.map((t) => (
                    <tr key={t.id} className="border-b border-white/5 last:border-0">
                      <td className="py-2 pr-4 client-line">{t.client || "—"}</td>
                      <td className="py-2 pr-4">{t.title}</td>
                      <td className="py-2 pr-4 text-white/60 font-mono text-xs">{fmtDate(t.deadline)}</td>
                      <td className="py-2 pr-4">
                        <span className="text-[0.7rem] uppercase tracking-widest text-white/70">{(t.status || "").replace("_", " ")}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Individual daily-work tracking (item 8a) — pulled from filed daily logs.
              Has its own member + date range controls so a manager/admin can quickly
              audit anyone's ledger without touching the KRA range above. Editors
              only see themselves (RBAC enforced on the backend). */}
          <WorkTrackingSection
            userId={userId || user?.id}
            userName={selectedUser?.name || user?.name}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />

          {/* NEW — Hours by Project (Client → Project rollup, no per-member scope
              because the point is to aggregate hours from every editor working
              on the same project). */}
          <HoursByProjectSection dateFrom={dateFrom} dateTo={dateTo} />
        </>
      ) : (
        <div className="text-white/40 text-sm">Select a member to see their report.</div>
      )}
    </div>
  );
}

// ============================================================
// Individual daily-work tracking (item 8a in the punch-list).
// Rows come from /reports/work-tracking, one row per task update
// or "other work done" entry — with client, project, and status.
// Filters (member + date range) live at the TOP of the Reports
// page to avoid duplication; this section just consumes them and
// exposes a grouping toggle + download.
// ============================================================
function WorkTrackingSection({ userId, userName, dateFrom, dateTo }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, complete: 0, incomplete: 0 });
  const [scope, setScope] = useState("day"); // day | week | month — grouping only
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!userId || !dateFrom || !dateTo) return;
    setLoading(true);
    api.get("/reports/work-tracking", { params: { user_id: userId, date_from: dateFrom, date_to: dateTo } })
      .then(({ data }) => { setRows(data.rows || []); setSummary(data.summary || { total: 0, complete: 0, incomplete: 0 }); })
      .catch(() => { setRows([]); setSummary({ total: 0, complete: 0, incomplete: 0 }); })
      .finally(() => setLoading(false));
  }, [userId, dateFrom, dateTo]);

  const downloadPdf = async () => {
    if (!userId) return;
    setDownloading(true);
    try {
      const res = await api.get("/reports/work-tracking-pdf", {
        params: { user_id: userId, date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const namePart = (userName || "member").replace(/\W+/g, "-");
      a.download = `LIM-Performance-${namePart}-${dateFrom}-to-${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Performance ledger downloaded");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Group rows by scope for the header aggregation
  const grouped = useMemo(() => {
    const bucketKey = (dateStr) => {
      const d = new Date(`${dateStr}T00:00:00`);
      if (scope === "day") return dateStr;
      if (scope === "week") {
        const day = d.getDay();
        const diff = (day + 6) % 7; // Monday
        const mon = new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
        return `Week of ${fmtDate(iso(mon))}`;
      }
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    };
    const out = {};
    rows.forEach((r) => {
      const k = bucketKey(r.date);
      (out[k] = out[k] || []).push(r);
    });
    return out;
  }, [rows, scope]);

  const statusChip = (s) => {
    const c = {
      ...Object.fromEntries(Object.entries(STATUS_META).map(([k, v]) => [k, v.pill.replace("border border-", "border-")])),
      leave: "bg-amber-500/10 text-amber-300 border-amber-500/30",
      weekly_off: "bg-slate-500/15 text-slate-200 border-slate-500/30",
      holiday: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    }[s] || "bg-white/10 text-white/60 border-white/15";
    return <span className={`text-[0.7rem] px-2 py-0.5 rounded border ${c}`}>{s.replace("_", " ")}</span>;
  };

  // Time tracker rollup: convert timer seconds into a half-day-anchored day
  // count. 4h → ½ day, 8h → 1 full day; the ledger + summary render both the
  // raw hours and this derived Days column so leaders can eyeball capacity.
  const daysFromSeconds = (secs) => {
    if (!secs) return 0;
    return Math.round((secs / 3600) / 4) * 0.5;
  };
  const formatDays = (d) => {
    if (!d) return "—";
    if (d === 0.5) return "½ day";
    if (d === 1) return "1 day";
    return `${d} days`;
  };
  const totalDays = useMemo(
    () => rows.reduce((acc, r) => acc + daysFromSeconds(r.seconds || 0), 0),
    [rows]
  );

  return (
    <div className="glass rounded-xl p-4" data-testid="work-tracking-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="overline">
            Work Tracking · Daily Log ledger{" "}
            <span className="text-white/40">{userName ? `· ${userName}` : ""}</span>
          </div>
          <div className="text-xs text-white/40 mt-0.5 font-mono">
            Uses the Member + date range picked above.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="work-tracking-pdf-btn"
            onClick={downloadPdf}
            disabled={downloading || !userId || rows.length === 0}
            className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#E11D2E]/40 bg-[#E11D2E]/10 text-[#E11D2E] hover:bg-[#E11D2E]/20 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <Download size={12} />
            {downloading ? "Generating…" : "Download PDF"}
          </button>
          <span className="text-[0.7rem] uppercase tracking-widest text-white/40 font-mono ml-1">Group by</span>
          {["day", "week", "month"].map((k) => (
            <button
              key={k}
              onClick={() => setScope(k)}
              data-testid={`work-tracking-scope-${k}`}
              className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                scope === k
                  ? "bg-white/10 border-white/25 text-white"
                  : "border-white/10 text-white/60 hover:border-white/30"
              }`}
            >
              {k === "day" ? "Daily" : k === "week" ? "Weekly" : "Monthly"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/60 mb-4 font-mono">
        <span>Total rows: <span className="text-white">{summary.total}</span></span>
        <span>Complete: <span className="text-emerald-300">{summary.complete}</span></span>
        <span>Pending: <span className="text-amber-300">{summary.incomplete}</span></span>
        <span>Hours logged: <span className="text-blue-300">{summary.total_hours_label || "—"}</span></span>
        <span>Days: <span className="text-sky-300">{totalDays ? formatDays(totalDays) : "—"}</span></span>
        <span>OT (½): <span className="text-cyan-300">{summary.ot_half_days || 0}</span></span>
        <span>OT (Full): <span className="text-[#E11D2E]">{summary.ot_full_days || 0}</span></span>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-white/40 text-sm italic border border-dashed border-white/8 rounded-md p-6 text-center">
          No daily-log entries filed in this range.
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="text-white/50 text-xs uppercase sticky top-0 bg-surface/95">
              <tr className="border-b border-white/5">
                <th className="text-left py-2 pr-4">Date</th>
                <th className="text-left py-2 pr-4">Client</th>
                <th className="text-left py-2 pr-4">Project</th>
                <th className="text-left py-2 pr-4">Work Done</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Hours</th>
                <th className="text-left py-2 pr-4">Days</th>
                <th className="text-left py-2 pr-4">OT</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([bucket, list]) => (
                <React.Fragment key={`grp-${bucket}`}>
                  {scope !== "day" && (
                    <tr key={`h-${bucket}`}>
                      <td colSpan="8" className="py-2 pt-4 text-[0.7rem] uppercase tracking-widest text-white/40">
                        {bucket} · {list.length} entries
                      </td>
                    </tr>
                  )}
                  {list.map((r, i) => (
                    <tr key={`${bucket}-${i}`} className="border-b border-white/5 last:border-0 align-top">
                      <td className="py-2 pr-4 text-white/60 font-mono text-xs whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="py-2 pr-4 text-white/70">{r.client || "—"}</td>
                      <td className="py-2 pr-4">{r.project || (r.kind === "leave" ? "—" : "—")}</td>
                      <td className="py-2 pr-4 text-white/60 leading-snug">
                        {r.work}
                        {r.reason && <div className="text-xs text-amber-300/70 mt-0.5">{r.reason}</div>}
                        {r.kind === "extra" && <span className="ml-2 text-[0.65rem] uppercase tracking-widest text-white/30">Other</span>}
                      </td>
                      <td className="py-2 pr-4">{statusChip(r.status || "")}</td>
                      <td className="py-2 pr-4 text-white/70 font-mono text-xs whitespace-nowrap">{r.hours || "—"}</td>
                      <td className="py-2 pr-4 text-sky-300/80 font-mono text-xs whitespace-nowrap" data-testid={`work-tracking-days-${bucket}-${i}`}>
                        {formatDays(daysFromSeconds(r.seconds || 0))}
                      </td>
                      <td className="py-2 pr-4 text-xs text-amber-300/80 whitespace-nowrap">{r.ot || "—"}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Hours by Project (Client → Project rollup)
// Answers: "How many hours have we spent on this specific project
// across every task and every team member?" — de-duplicates work
// that was accidentally created as two different task titles.
// ============================================================
function HoursByProjectSection({ dateFrom, dateTo }) {
  const [data, setData] = useState({ rows: [], grand_total_hours: 0, grand_total_days: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    api.get("/reports/hours-by-project", { params: { date_from: dateFrom, date_to: dateTo } })
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ rows: [], grand_total_hours: 0, grand_total_days: 0 }))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const fmtHours = (s) => {
    if (!s) return "—";
    return `${(s / 3600).toFixed(2)} h`;
  };
  const daysFromSecs = (s) => (s ? Math.round((s / 3600) / 4) * 0.5 : 0);
  const fmtDays = (d) => (d ? (d === 0.5 ? "½ day" : d === 1 ? "1 day" : `${d} days`) : "—");

  // Group rows by client for a two-level table.
  const byClient = data.rows.reduce((acc, r) => {
    (acc[r.client] = acc[r.client] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="glass rounded-xl p-4" data-testid="hours-by-project-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg tracking-tight">Hours by Project</h3>
        <div className="text-xs text-white/60 flex items-center gap-4">
          <span>Total: <span className="text-sky-300">{fmtDays(data.grand_total_days || 0)}</span></span>
          <span>Hours: <span className="text-blue-300">{data.grand_total_hours || 0}h</span></span>
        </div>
      </div>
      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : data.rows.length === 0 ? (
        <div className="text-white/40 text-sm">No hours tracked in this range yet.</div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-[0.7rem] uppercase tracking-widest text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left py-2 pr-4">Client / Project</th>
                <th className="text-left py-2 pr-4">Tasks</th>
                <th className="text-left py-2 pr-4">Hours</th>
                <th className="text-left py-2 pr-4">Days</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(byClient).sort((a, b) => a.localeCompare(b)).map((client) => (
                <React.Fragment key={client}>
                  <tr>
                    <td colSpan="4" className="py-2 pt-4 text-[0.7rem] uppercase tracking-widest text-white/40">{client}</td>
                  </tr>
                  {byClient[client].map((r) => {
                    const key = `${r.client}::${r.project_id || "unspec"}`;
                    return (
                      <React.Fragment key={key}>
                        <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`hbp-row-${key}`}>
                          <td className="py-2 pr-4">
                            <button
                              type="button"
                              onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                              className="text-white/90 hover:text-[#E11D2E] flex items-center gap-1.5"
                            >
                              <span className={`text-[0.65rem] transition-transform ${expanded[key] ? "rotate-90" : ""}`}>▶</span>
                              <span className="font-medium">{r.project_name || "Unspecified"}</span>
                            </button>
                          </td>
                          <td className="py-2 pr-4 text-white/60 font-mono text-xs">{r.task_count}</td>
                          <td className="py-2 pr-4 text-blue-300 font-mono text-xs">{fmtHours(r.total_seconds)}</td>
                          <td className="py-2 pr-4 text-sky-300/80 font-mono text-xs">{fmtDays(daysFromSecs(r.total_seconds))}</td>
                        </tr>
                        {expanded[key] && r.tasks.map((t) => (
                          <tr key={t.id} className="bg-black/25 border-b border-white/5">
                            <td className="py-1.5 pr-4 pl-6 text-xs text-white/70 truncate"><ClientTag client={t.client} />{t.title}</td>
                            <td className="py-1.5 pr-4 text-[0.7rem] text-white/50 uppercase tracking-widest">{(t.status || "").replace("_", " ")}</td>
                            <td className="py-1.5 pr-4 text-xs text-blue-300/80 font-mono">{fmtHours(t.seconds)}</td>
                            <td className="py-1.5 pr-4 text-xs text-sky-300/70 font-mono">{fmtDays(daysFromSecs(t.seconds))}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Every Monday-morning submission PDF lives here — grouped by team + week. */}
      <SubmissionsArchive />
    </div>
  );
}
